import React from 'react';
import { useDrop } from 'react-dnd';
import { ItemType } from 'src/models/item';
import { DropData, SimpleDropData } from 'src/server/item-drag-drop';
import { DraggableContainerProps } from './DragDrop/DraggableContainer';

export interface ItemDropTargetProps {
  targetKeys: ItemType[];
  dropData: SimpleDropData;
}

export const ItemDropTarget: React.FC<ItemDropTargetProps> = ({
  children,
  targetKeys,
  dropData,
}) => {
  type DragItem = DraggableContainerProps['dragData'];

  const validDrop = (item: DragItem) => {
    if (!item) return false;

    const salesOrderItem =
      item.externalId != null && item.externalId !== '';

    if (dropData.type === 'clipboard' && salesOrderItem) {
      return false;
    }

    const sameResourceDrop =
      dropData.type === 'resource' && item.sourceResourceId === dropData.resourceId;

    if (salesOrderItem && !sameResourceDrop) {
      return false;
    }

    if (
      dropData.type === 'resource' &&
      dropData.salesOrderLane &&
      !salesOrderItem
    ) {
      return false;
    }

    // Items dragged off the clipboard have no source resource, so the
    // same-resource exemption can't apply. Processes without an inventory
    // link (null workstation) may return to any lane; linked processes must
    // still match the lane's workstation.
    const fromClipboard = item.sourceResourceId === null;

    const validResourceDrop =
      dropData.type === 'resource' &&
      (sameResourceDrop ||
        item.inventoryWorkstationIds.every(
          (x) =>
            x === dropData.inventoryWorkstationId ||
            (fromClipboard && x === null),
        ));

    const validRunDrop =
      dropData.type === 'run' &&
      item.inventoryWorkstationIds.every(
        (x) => x === dropData.resourceInventoryWorkstationId,
      );

    const validClipboardDrop = dropData.type === 'clipboard';

    return validResourceDrop || validRunDrop || validClipboardDrop;
  };

  const [{ isOver }, dropRef] = useDrop({
    accept: targetKeys,
    canDrop: (data?: { data: DragItem }) => !!data && validDrop(data.data),
    drop: (_item, monitor): DropData<SimpleDropData> | undefined => {
      if (!monitor.isOver({ shallow: true })) return undefined;
      const initialOffset = monitor.getInitialSourceClientOffset();
      const difference = monitor.getDifferenceFromInitialOffset();

      return {
        ...dropData,
        clientOffset: {
          x: initialOffset!.x + difference!.x,
          y: initialOffset!.y + difference!.y,
        },
      };
    },
    collect: (monitor) => {
      const item = monitor.getItem<{ data: DragItem }>()?.data;
      return {
        isOver: monitor.isOver({ shallow: true }) && validDrop(item),
      };
    },
  });

  if (targetKeys.length === 0) {
    return <>{children}</>;
  }

  return (
    <div ref={dropRef} className={isOver ? 'highlighted' : ''}>
      {children}
    </div>
  );
};
